import { Video } from "lucide-react";
import { Badge } from "@/components/ui/others/Badges";
import { useVideoChildModuleData } from "@/module/videoProduction/helpers/useVideoChildModuleData";

const PAYMENT_STATUS_BADGE: Record<
  string,
  { label: string; bg: string; text: string }
> = {
  advance: { label: "Advance", bg: "#e0f2fe", text: "#0369a1" },
  partial: { label: "Partial", bg: "#fef3c7", text: "#92400e" },
  full: { label: "Paid", bg: "#dcfce7", text: "#166534" },
  pending: { label: "Pending", bg: "#fee2e2", text: "#991b1b" },
};

export default function VideoProductionChildTrigger({
  child,
  isOpen,
  isDirty,
  onUpdateClick,
}: {
  child: any;
  isOpen: boolean;
  isDirty?: boolean;
  onUpdateClick?: (e: React.MouseEvent) => void;
}) {
  const isActive = !child?.isFroze;
  const { serviceName, variantName } = useVideoChildModuleData(child);

  const statusName = child?.status?.name;
  const subStatusName = child?.subStatus?.name;
  const paymentStatus = child?.paymentStatus || "pending";
  const psBadge =
    PAYMENT_STATUS_BADGE[paymentStatus] || PAYMENT_STATUS_BADGE.pending;

  return (
    <div className="flex gap-2.5 items-center w-full">
      <div
        className={`rounded-full p-2 shrink-0 ${
          isActive ? "bg-[var(--color-green-1)]" : "bg-[var(--color-black-1)]"
        }`}
      >
        <Video
          size={16}
          className={
            isActive
              ? "text-[var(--color-primary)]"
              : "text-[var(--color-black-7)]"
          }
          strokeWidth={1.5}
        />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-[14px] font-medium text-[var(--color-black-10)] tracking-[-0.28px] leading-tight truncate">
            {child?.name || "--"}
          </p>
          {!isOpen && statusName && (
            <div className="flex gap-1 items-center shrink-0">
              <Badge className="bg-[#dde9ff] text-[#335cff] text-[10px] font-medium px-1.5 py-[1px] rounded-full">
                {statusName}
              </Badge>
              {subStatusName && (
                <Badge className="text-[#ff8447] text-[10px] font-medium px-1.5 py-[1px] rounded-full bg-transparent">
                  {subStatusName}
                </Badge>
              )}
              <Badge
                className="text-[9px] font-bold px-1.5 py-[1px] rounded-full"
                style={{ backgroundColor: psBadge.bg, color: psBadge.text }}
              >
                {psBadge.label}
              </Badge>
            </div>
          )}
        </div>
        <p className="text-[12px] text-[var(--color-black-7)] tracking-[-0.24px] leading-tight mt-[1px]">
          {[serviceName, variantName].filter(Boolean).join(" • ")}
        </p>
      </div>

      {isDirty ? (
        <Badge
          className="text-[11px] font-semibold tracking-[-0.2px] px-3 py-1 rounded-full shrink-0 bg-[#267355] text-white cursor-pointer hover:bg-[#1e5c44] transition-colors animate-in fade-in duration-200"
          onClick={onUpdateClick}
        >
          Update
        </Badge>
      ) : (
        <Badge
          className={`text-[10px] font-medium tracking-[-0.2px] px-2 py-[2px] rounded-full shrink-0 ${
            isActive
              ? "bg-[#c2f5da] text-[#0b4627]"
              : "bg-[#e1e4ea] text-[#222530]"
          }`}
        >
          {isActive ? "Active" : "Paused"}
        </Badge>
      )}
    </div>
  );
}
